import { beginRegistrationAttempt, getPendingSegment, buildSegmentOAuthState } from '../features/product-segment'
import { useDraftStore } from '../features/onboarding'
// Yandex Sign-In — top-level redirect flow (parent bd-1q4). iOS Safari blocks
// window.open() and nulls window.opener after the Yandex cross-origin redirect,
// so the old popup+postMessage flow was broken on iPhones. We now navigate the
// whole tab to Yandex; the backend callback pre-exchanges the code and redirects
// back to /?auth_token=...&state=..., captured by useOAuthRedirect on load.
//
// Native (Capacitor, Phase 2.5): we can't navigate the in-app WebView to Yandex
// (its origin is the synthetic humanteq.app, and the return to the app must come
// through the SYSTEM browser so Android's intent-filter fires). So open Yandex in
// the system browser; the state carries a `native:` prefix that tells the backend
// to exchange the code and 302 to com.humanteq.app://oauth/yandex, which Android
// opens into the app (appUrlOpen in main.jsx).

import { useCallback, useState } from 'react'
import { Browser } from '@capacitor/browser'
import { authApi } from '../api/auth'
import { isNative, OAUTH_REDIRECT_URI } from '../lib/platform'

function makeState(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return Date.now().toString(36) + Math.random().toString(36).slice(2)
}

// #4480 feature-overlay tag: the redirect flow has no request body, so the
// build's variant (VITE_APP_VARIANT) rides as a '~f:<name>' suffix inside
// state — the backend callback reads it to tag the NEW account at creation
// and echoes state back verbatim; applyOAuthParams validates by exact match
// against this same string, so the suffix changes nothing for it.
export function composeOAuthState(native: boolean, variant = ''): string {
  const base = native ? `native:${makeState()}` : makeState()
  const v = variant.trim()
  return v ? `${base}~f:${encodeURIComponent(v)}` : base
}

export function useYandexAuth() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const signIn = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { client_id: clientId } = await authApi.yandexConfig()
      if (!clientId) throw new Error('Yandex Sign-In недоступно')

      // State guards against CSRF; sessionStorage survives the redirect round-trip.
      // The `native:` prefix tells the backend callback to 302 to the app scheme
      // instead of the browser HTML redirect (Phase 2.5).
      // Segment state carries the product segment (backend bridges break_up
      // to the feature tag); builds without a captured segment keep the
      // VITE_APP_VARIANT '~f:' suffix.
      const attempt = beginRegistrationAttempt(getPendingSegment(), useDraftStore.getState().draft)
      const variant = String(import.meta.env.VITE_APP_VARIANT || '').trim()
      const state = attempt.segment !== 'humanteq' || !variant
        ? buildSegmentOAuthState(attempt.segment, isNative, attempt.id)
        : composeOAuthState(isNative, variant)
      sessionStorage.setItem('yandex_oauth_state', state)

      const redirectUri = encodeURIComponent(OAUTH_REDIRECT_URI)
      const authUrl = `https://oauth.yandex.ru/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${encodeURIComponent(state)}`

      if (isNative) {
        // System browser so the backend's 302 to the app scheme is resolved by
        // Android and opens the app (in-WebView navigation can't trigger it).
        await Browser.open({ url: authUrl })
        setLoading(false)
      } else {
        // Top-level navigation — no popup, no postMessage, no popup-closed polling.
        window.location.href = authUrl
      }
    } catch (e) {
      setError((e as Error).message || 'Ошибка входа через Yandex')
      setLoading(false)
    }
  }, [])

  return { signIn, loading, error }
}
